import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MomentModule } from '@fredjohnd/ngx-moment';

import { StepCardViewData } from '@shared/components/approval-workflow-steps/models/step-card.model';
import { ApprovalStatusEnum } from '@shared/components/generate-approval-workflow/enum/generate-approval-workflow.enum';
import {
  ApprovalStepsModel,
  ApproverModel
} from '@shared/components/generate-approval-workflow/model/generate-approval-workflow.model';
import { BlurOnModalCloseDirective } from '@shared/directives/blur-on-modal-close.directive';
import { WheelHorizontalScrollDirective } from '@shared/directives/wheel-horizontal-scroll.directive';
import { RequesterTypeEnum } from '@shared/enum/requester-type.enum';
import { ApprovalFlowResponse } from '@shared/models/approval-flow.model';
import { PipesModule } from '@shared/pipes/pipes.module';

import { NoteToApproverCardComponent } from './components/note-to-approver-card/note-to-approver-card.component';
import { RequesterCardComponent } from './components/requester-card/requester-card.component';
import { StepCardComponent } from './components/step-card/step-card.component';

@Component({
  selector: 'app-approval-workflow-steps',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MomentModule,
    PipesModule,
    RequesterCardComponent,
    NoteToApproverCardComponent,
    StepCardComponent,
    WheelHorizontalScrollDirective,
    BlurOnModalCloseDirective
  ],
  templateUrl: './approval-workflow-steps.component.html',
  styleUrls: ['./approval-workflow-steps.component.scss']
})
export class ApprovalWorkflowStepsComponent {
  @Input() isCreateMode = false;
  @Input() approvalSteps: ApprovalStepsModel[] = [];
  @Input() approvalWorkflow?: ApprovalFlowResponse;
  @Input() documentStoreCode?: string;
  @Input() documentStoreName?: string;
  @Input() isFlowComplete = false;
  @Input() noteText = '';
  @Input() canRegenerate = false;
  @Output() noteTextChange = new EventEmitter<string>();
  @Output() regenerate = new EventEmitter<void>();

  get requesterType(): RequesterTypeEnum {
    return this.approvalWorkflow?.requesterType;
  }

  get requesterDate(): string {
    return this.approvalWorkflow?.createdDate;
  }

  // Store requester label per spec: "{Store Code}-{Store Name}".
  get storeName(): string | undefined {
    if (this.approvalWorkflow?.requesterType !== RequesterTypeEnum.STORE) {
      return undefined;
    }
    const code = this.documentStoreCode;
    const name = this.documentStoreName;
    return code && name ? `${code}-${name}` : name || code;
  }

  // Complete = explicit input OR every step approved (derived, so it survives async input arrival).
  get activeFlowComplete(): boolean {
    const steps = this.activeSteps;
    return (
      this.isFlowComplete ||
      (steps.length > 0 && steps.every(step => step.approvalStatus === ApprovalStatusEnum.APPROVED))
    );
  }

  get showRegenerateButton(): boolean {
    return this.canRegenerate;
  }

  private get activeSteps(): ApprovalStepsModel[] {
    return this.approvalSteps?.length ? this.approvalSteps : this.approvalWorkflow?.steps || [];
  }

  get displayStepCards(): StepCardViewData[] {
    return this.activeSteps.reduce<StepCardViewData[]>((cards, step) => {
      return cards.concat({
        stepName: step.name,
        supervisorLevel: step.supervisorLevel,
        status: step.approvalStatus,
        actionedAt: this.getStepDate(step),
        comment: step.comment,
        autoApprove: step.autoApprove,
        autoApprovalLimit: step.autoApprovalLimit,
        approvers: this.sortApprovers(step.approvers)
      });
    }, []);
  }

  private sortApprovers(approvers: ApproverModel[] | undefined): ApproverModel[] {
    return [...(approvers || [])].sort((a, b) => Number(b.approve !== null) - Number(a.approve !== null));
  }

  private getStepDate(step: ApprovalStepsModel): string | undefined {
    switch (step.approvalStatus) {
      case ApprovalStatusEnum.APPROVED:
        return step.approvedAt;
      case ApprovalStatusEnum.REJECTED:
        return step.rejectedAt;
      default:
        return undefined;
    }
  }

  onRegenerate(): void {
    this.regenerate.emit();
  }

  onNoteChange(val: string): void {
    this.noteText = val;
    // Always emit (even empty) so the parent clears a stale note when the user deletes it.
    this.noteTextChange.emit(val.trim());
  }
}
